import { OwnProps } from "@insite/client-framework/Components/Zone";
import { getOrderStatusMappings } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridItem from "@insite/mobius/GridItem";
import Select, { SelectProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addOrderStatus } from "@tSchaedlerYesco/Store/Data/PlaceOrder/Handlers/AddOrderStatus";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const canSelectOrderStatus = !getCurrentUserIsGuest(state);
    const orderStatuses = getOrderStatusMappingDataView(state).value;
    const defaultOrderStatus = orderStatuses?.find(op => op.isDefault === true);
    return {
        canSelectOrderStatus,
        orderStatusMappings: getOrderStatusMappingDataView(state).value,
        defaultERPOrderStatus: defaultOrderStatus?.erpOrderStatus || "B",
    };
};

export interface CustomOrderStatusStyles {
    container?: InjectableCss;
}

export const CustomOrderStatusStyles: CustomOrderStatusStyles = {
    container: {
        css: css`
            flex-basis: 50%;
            max-width: 50%;
            padding-top: 0px;
            > div[class^="FormFieldStyle"] {
                width: auto;
                > div[class^="FormInputWrapper"] {
                    > select {
                        padding-right: 40px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }
        `,
    },
};

const mapDispatchToProps = {
    addOrderStatus,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const CheckoutReviewAndSubmitOrderStatus: FC<Props> = ({
    addOrderStatus,
    canSelectOrderStatus,
    orderStatusMappings,
    defaultERPOrderStatus,
}) => {
    const [selectedOrderStatus, setOrderStatus] = useState("");
    const options = orderStatusMappings || [];
    const defaultOrderStats = defaultERPOrderStatus;

    useEffect(() => {
        if (!selectedOrderStatus && selectedOrderStatus !== defaultERPOrderStatus) {
            addOrderStatus({ orderStatus: defaultOrderStats });
        }
    });

    const handleOrderStatusChange: SelectProps["onChange"] = event => {
        const status = options.find(c => c.erpOrderStatus === event.target.value);
        if (status) {
            setOrderStatus(status.erpOrderStatus);
            addOrderStatus({ orderStatus: status.erpOrderStatus });
        }
    };

    const styles = CustomOrderStatusStyles;
    return (
        <>
            {canSelectOrderStatus && options && (
                <GridItem {...styles.container}>
                    <Select
                        label="Order Status"
                        value={selectedOrderStatus || ""}
                        disabled={false}
                        onChange={handleOrderStatusChange}
                    >
                        {options.map(co => (
                            <option key={co.id} value={co.erpOrderStatus}>
                                {co.displayName}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {!canSelectOrderStatus && options && addOrderStatus({ orderStatus: defaultOrderStats })}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitOrderStatus),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
        displayName: "Order Status Form",
    },
};

export default widgetModule;
